import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { useToast } from '@/hooks/useToast';
import { SectionHeader, Field } from './SectionHeader';
import type { DeviceStatus } from '@/lib/types';

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'] as const;
const DAY_LABEL = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

interface Props { device: DeviceStatus; disabled: boolean }

export function ScheduleSection({ device, disabled }: Props) {
  const sched = device.schedule ?? {};
  const [time1, setTime1] = useState(sched.time1 ?? '');
  const [time2, setTime2] = useState(sched.time2 ?? '');
  const [days,  setDays]  = useState<Set<string>>(new Set(sched.days ?? []));
  const toast = useToast();

  // Re-sync if device payload changes
  useEffect(() => { setTime1(device.schedule?.time1 ?? ''); }, [device.schedule?.time1]);
  useEffect(() => { setTime2(device.schedule?.time2 ?? ''); }, [device.schedule?.time2]);
  useEffect(() => { setDays(new Set(device.schedule?.days ?? [])); }, [device.schedule?.days]);

  const mut = useDeviceMutation<{ time1: string; time2: string; days: string }>(
    device.client_id, 'schedule', { successToast: 'Schedule saved' },
  );

  const toggle = (d: string) => {
    setDays((s) => {
      const n = new Set(s);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });
  };

  const submit = () => {
    if (!time1 && !time2) { toast.warn('Enter at least one clean time'); return; }
    if (days.size === 0) { toast.warn('Select at least one day'); return; }
    mut.mutate({ time1, time2, days: Array.from(days).join(',') });
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="Cleaning schedule"
        description="Up to two clean times per day, on the days you select." />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Clean time 1" htmlFor="t1">
          <Input id="t1" type="time" value={time1} disabled={disabled}
            onChange={(e) => setTime1(e.target.value)} />
        </Field>
        <Field label="Clean time 2" htmlFor="t2">
          <Input id="t2" type="time" value={time2} disabled={disabled}
            onChange={(e) => setTime2(e.target.value)} />
        </Field>
      </div>
      <div>
        <span className="block text-xs font-semibold text-slate-700 mb-2">Days of week</span>
        <div className="grid grid-cols-7 gap-1.5">
          {DAY_NAMES.map((d, i) => {
            const active = days.has(d);
            return (
              <button
                key={d} type="button" disabled={disabled}
                onClick={() => toggle(d)}
                className={
                  'h-10 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ' +
                  (active ? 'bg-ink text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                }
              >
                {DAY_LABEL[i]}
              </button>
            );
          })}
        </div>
      </div>
      <Button onClick={submit} loading={mut.isPending} disabled={disabled || mut.isPending}>
        Save schedule
      </Button>
    </div>
  );
}
